import {Button, Field, Fieldset, HStack, Icon, Input, RadioGroup, Stack, Text, Textarea} from "@chakra-ui/react";
import {useCallback, useState} from "react";
import {DocumentId} from "platformed-client/types";
import {useValidation, useValidatedPromiseState} from "../../../hooks/validationState";
import {useBannedEmailValidation} from "../../../hooks/useBannedEmailValidation";
import externalApi from "../../../api/external";
import {useSessionState} from "../../../hooks/sessionState";
import {useClientAccount} from "../../hooks/clientAccount";
import {withDialog} from "../../../state/withDialog";
import {Type} from "../../../utils/typing";
import {q, useQueryData} from "../../../state";
import {PencilSquareIcon} from "@heroicons/react/20/solid";
import RestrictedDocumentSelector from "./RestrictedDocumentSelector";
import {DialogBody, DialogFooter, DialogHeader, DialogTitle} from "../../../components/ui/dialog";
import {Alert} from "../../../components/ui/alert";
import T from "platformed-components/i18n/T";
import useTranslate from "platformed-components/i18n/useTranslate";

type RequestDocumentAccessDialogProps = {onClose: () => void; initialSelectedDocumentIds: DocumentId[]};

const RequestDocumentAccessDialogSuccess = ({onClose, nda}: {onClose: () => void; nda: boolean}) => {
  const t = useTranslate();

  let title = t("c.Access_request_sent");

  if (nda) {
    title += " - " + t("c.NDA_required");
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <Stack gap={4}>
          <Alert status="success">
            <T keyName="trust_center.request_access.success_message" />
          </Alert>
          {nda ? (
            <p>
              <T keyName="trust_center.request_access.nda_success_message" />
            </p>
          ) : (
            <p>
              <T keyName="trust_center.request_access.approval_message" />
            </p>
          )}
        </Stack>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          <T keyName="c.Close" />
        </Button>
      </DialogFooter>
    </>
  );
};

type RequestDocumentAccessDialogFormProps = RequestDocumentAccessDialogProps & {
  onSuccess: (data: {nda: boolean}) => void;
};

const RequestDocumentAccessDialogForm = ({
  onSuccess,
  onClose,
  initialSelectedDocumentIds,
}: RequestDocumentAccessDialogFormProps) => {
  const account = useClientAccount();
  const documents = useQueryData(q.external.trustCenterDocuments(account)).filter(d => !d.is_readable);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState(initialSelectedDocumentIds);

  const requiresEsignature = selectedDocumentIds
    .map(id => documents.find(d => d.document_id === id)!)
    .some(d => d.requires_esignature);

  const [email, setEmail] = useSessionState("requestDocumentAccessDialogEmail", Type.String(), "");
  const [name, setName] = useSessionState("requestDocumentAccessDialogName", Type.String(), "");
  const [phone, setPhone] = useSessionState("requestDocumentAccessDialogPhone", Type.String(), "");
  const [pointOfContact, setPointOfContact] = useSessionState(
    "requestDocumentAccessDialogPointOfContact",
    Type.String(),
    "",
  );
  const [message, setMessage] = useSessionState("requestDocumentAccessDialogMessage", Type.String(), "");
  const [alreadySignedNda, setAlreadySignedNda] = useSessionState(
    "requestDocumentAccessDialogAlreadySignedNda",
    Type.Boolean(),
    false,
  );

  const [validationErrors, setValidationErrors] = useValidation(useState({}), {email, name});
  const [sending, send] = useValidatedPromiseState(
    async () => {
      await externalApi.trustCenters.requestDocumentAccess(account, {
        document_ids: selectedDocumentIds,
        email,
        name,
        phone,
        point_of_contact: pointOfContact,
        message,
        already_signed_nda: alreadySignedNda,
      });

      onSuccess({nda: requiresEsignature && !alreadySignedNda});
    },
    [
      account,
      selectedDocumentIds,
      email,
      name,
      phone,
      pointOfContact,
      message,
      alreadySignedNda,
      onSuccess,
      requiresEsignature,
    ],
    setValidationErrors,
  );

  const isBannedEmails = useBannedEmailValidation(email);

  return (
    <form onSubmit={send}>
      <DialogHeader>
        <DialogTitle>
          <T keyName="trust_center.request_access.dialog_title" />
        </DialogTitle>
      </DialogHeader>
      <DialogBody>
        <HStack gap={8}>
          <Fieldset.Root alignSelf="stretch" display="flex" flexDirection="column" flex="1">
            <Fieldset.Legend>
              <T keyName="trust_center.request_access.select_documents" />
            </Fieldset.Legend>
            <Fieldset.Content>
              <RestrictedDocumentSelector
                documents={documents}
                value={selectedDocumentIds}
                onChange={setSelectedDocumentIds}
              />
            </Fieldset.Content>
          </Fieldset.Root>
          <Fieldset.Root flex="1">
            <Fieldset.Legend>
              <T keyName="trust_center.request_access.fill_out_details" />
            </Fieldset.Legend>
            <Fieldset.Content>
              <Stack gap={0} rounded="md">
                {requiresEsignature && (
                  <Stack fontSize="md" p={4} px={6} bg="blue.100" rounded="md" color="blue.800" gap={4} my={4}>
                    <Stack gap={2}>
                      <HStack gap={6}>
                        <Icon as={PencilSquareIcon} fontSize="xl" boxSize="1em" />
                        <Text fontWeight="600">
                          <T keyName="c.Requires_NDA" />
                        </Text>
                      </HStack>
                      <Text>
                        <T keyName="trust_center.request_access.nda_required_message" />
                      </Text>
                    </Stack>
                    <Field.Root>
                      <RadioGroup.Root
                        value={alreadySignedNda ? "yes" : "no"}
                        onValueChange={e => setAlreadySignedNda(e.value === "yes")}
                      >
                        <Stack gap={1} fontWeight="500">
                          <RadioGroup.Item value="no" borderColor="blue.800">
                            <RadioGroup.ItemHiddenInput />
                            <RadioGroup.ItemIndicator />
                            <RadioGroup.ItemText>
                              <T keyName="trust_center.request_access.send_nda_by_email" />
                            </RadioGroup.ItemText>
                          </RadioGroup.Item>
                          <RadioGroup.Item value="yes" borderColor="blue.800">
                            <RadioGroup.ItemHiddenInput />
                            <RadioGroup.ItemIndicator />
                            <RadioGroup.ItemText>
                              <T keyName="trust_center.request_access.already_signed_nda" />
                            </RadioGroup.ItemText>
                          </RadioGroup.Item>
                        </Stack>
                      </RadioGroup.Root>
                    </Field.Root>
                  </Stack>
                )}
                <Stack gap={4} mt={4}>
                  <Field.Root required invalid={isBannedEmails || !!validationErrors?.email?.[0]}>
                    <Field.Label>
                      <T keyName="c.Your_email" />
                      <Field.RequiredIndicator />
                    </Field.Label>
                    <Input value={email} onChange={e => setEmail(e.target.value)} autoFocus />
                    {validationErrors?.email?.[0] && (
                      <Field.ErrorText>{validationErrors.email[0].message}</Field.ErrorText>
                    )}
                    {isBannedEmails && (
                      <Field.ErrorText>
                        <T keyName="trust_center.request_access.banned_email_error" />
                      </Field.ErrorText>
                    )}
                    <Field.HelperText>
                      <T keyName="trust_center.request_access.approval_message" />
                    </Field.HelperText>
                  </Field.Root>
                  <Field.Root required>
                    <Field.Label>
                      <T keyName="c.Your_name" />
                      <Field.RequiredIndicator />
                    </Field.Label>
                    <Input value={name} onChange={e => setName(e.target.value)} />
                    {validationErrors.name && validationErrors.name[0] && (
                      <Field.ErrorText>{validationErrors.name[0].message}</Field.ErrorText>
                    )}
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>
                      <T keyName="c.Your_phone_number" />
                    </Field.Label>
                    <Input value={phone} onChange={e => setPhone(e.target.value)} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>
                      <T
                        keyName="trust_center.request_access.point_of_contact_at"
                        params={{name: account.display_name}}
                      />
                    </Field.Label>
                    <Input value={pointOfContact} onChange={e => setPointOfContact(e.target.value)} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>
                      <T keyName="c.Additional_information" />
                    </Field.Label>
                    <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={6} />
                  </Field.Root>
                  {sending.lastError ? (
                    <Alert status="error" rounded="md" title={<T keyName="c.Error_sending_request" />}>
                      {`${sending.lastError}`}
                    </Alert>
                  ) : null}
                </Stack>
              </Stack>
            </Fieldset.Content>
          </Fieldset.Root>
        </HStack>
      </DialogBody>
      <DialogFooter>
        <HStack gap="3">
          <Button variant="outline" onClick={onClose}>
            <T keyName="c.Cancel" />
          </Button>
          <Button
            type="submit"
            colorPalette="blue"
            loading={sending.inProgress}
            disabled={sending.inProgress || selectedDocumentIds.length === 0}
          >
            <T keyName="c.Send_request" />
          </Button>
        </HStack>
      </DialogFooter>
    </form>
  );
};

export const RequestDocumentAccessDialog = withDialog(
  (props: RequestDocumentAccessDialogProps) => {
    type Stage = {type: "form"} | {type: "success"; nda: boolean};

    const [stage, setStage] = useState<Stage>({type: "form"});

    const handleSuccess = useCallback(({nda}: {nda: boolean}) => {
      setStage({type: "success", nda});
    }, []);

    return (
      <>
        {stage.type === "form" && <RequestDocumentAccessDialogForm {...props} onSuccess={handleSuccess} />}
        {stage.type === "success" && <RequestDocumentAccessDialogSuccess onClose={props.onClose} nda={stage.nda} />}
      </>
    );
  },
  {size: "xl", closeOnInteractOutside: false},
);
